import maplibregl from 'maplibre-gl'
import type { FeatureStyle } from '../../../database/schema.js'

export function getFillPaintProperties(
  style: FeatureStyle | undefined,
  defaultColor: string
): { fillColor: string; fillOpacity: number } {
  const fillPattern = style?.fillPattern
  if (fillPattern === 'none') {
    return { fillColor: 'transparent', fillOpacity: 0 }
  }
  return {
    fillColor: style?.fillColor ?? defaultColor,
    fillOpacity: style?.fillOpacity ?? 0.3,
  }
}

export function getFillStyleOptions(
  style: FeatureStyle | undefined,
  defaultColor: string
): { fillColor: string; fillOpacity: number } {
  return getFillPaintProperties(style, defaultColor)
}

export type FillPatternType = 'solid' | 'hatch' | 'cross-hatch' | 'dots' | 'diagonal' | 'none'

const registeredPatterns = new Set<string>()

/**
 * Build pattern pixel data directly to avoid canvas anti-aliasing artifacts
 * at tile boundaries. Each pixel's alpha is computed from its distance to
 * the nearest diagonal line center, giving crisp, perfectly-tileable results.
 */
export function createPatternData(
  pattern: FillPatternType,
  color: string,
  opacity: number
): { data: Uint8Array; size: number } {
  const size = pattern === 'diagonal' ? 16 : 8
  const r = parseInt(color.slice(1, 3), 16)
  const g = parseInt(color.slice(3, 5), 16)
  const b = parseInt(color.slice(5, 7), 16)
  const pixels = new Uint8Array(size * size * 4)

  switch (pattern) {
    case 'hatch':
      fillDiagonalLines(pixels, size, size, 1.5, r, g, b, opacity)
      break

    case 'cross-hatch':
      fillDiagonalLines(pixels, size, size, 1.0, r, g, b, opacity)
      fillReverseDiagonalLines(pixels, size, size, 1.0, r, g, b, opacity)
      break

    case 'dots':
      fillDots(pixels, size, r, g, b, opacity)
      break

    case 'diagonal':
      fillDiagonalLines(pixels, size, size, 2.0, r, g, b, opacity)
      break
  }

  return { data: pixels, size }
}

/**
 * For each pixel, compute distance to nearest 45° line (y = x + offset).
 * Distance from point (px, py) to line y = x + c is |py - px - c| / sqrt(2).
 * Lines repeat every `spacing` pixels, so offset c = 0, ±spacing, ±2*spacing, ...
 * A pixel within lineWidth/2 of the line center gets full alpha;
 * pixels at the boundary get partial alpha for soft edges.
 */
export function fillDiagonalLines(
  pixels: Uint8Array,
  size: number,
  spacing: number,
  lineWidth: number,
  r: number,
  g: number,
  b: number,
  opacity: number
): void {
  const halfW = lineWidth / 2
  const invSqrt2 = 1 / Math.sqrt(2)

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Distance to nearest line y = x + n*spacing (mod spacing)
      let d = (((y - x) % spacing) + spacing) % spacing
      if (d > spacing / 2) {
        d = spacing - d
      }
      const dist = d * invSqrt2

      if (dist < halfW + 0.5) {
        const alpha = dist <= halfW ? 1.0 : halfW + 0.5 - dist
        const a = Math.round(alpha * opacity * 255)
        if (a > 0) {
          const idx = (y * size + x) * 4
          // Composite: max of existing and new (for cross-hatch overlay)
          pixels[idx] = Math.max(pixels[idx], r)
          pixels[idx + 1] = Math.max(pixels[idx + 1], g)
          pixels[idx + 2] = Math.max(pixels[idx + 2], b)
          pixels[idx + 3] = Math.max(pixels[idx + 3], a)
        }
      }
    }
  }
}

/** Same as above but for lines going y = -x + c (top-right to bottom-left). */
export function fillReverseDiagonalLines(
  pixels: Uint8Array,
  size: number,
  spacing: number,
  lineWidth: number,
  r: number,
  g: number,
  b: number,
  opacity: number
): void {
  const halfW = lineWidth / 2
  const invSqrt2 = 1 / Math.sqrt(2)

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let d = (((y + x) % spacing) + spacing) % spacing
      if (d > spacing / 2) {
        d = spacing - d
      }
      const dist = d * invSqrt2

      if (dist < halfW + 0.5) {
        const alpha = dist <= halfW ? 1.0 : halfW + 0.5 - dist
        const a = Math.round(alpha * opacity * 255)
        if (a > 0) {
          const idx = (y * size + x) * 4
          pixels[idx] = Math.max(pixels[idx], r)
          pixels[idx + 1] = Math.max(pixels[idx + 1], g)
          pixels[idx + 2] = Math.max(pixels[idx + 2], b)
          pixels[idx + 3] = Math.max(pixels[idx + 3], a)
        }
      }
    }
  }
}

export function fillDots(
  pixels: Uint8Array,
  size: number,
  r: number,
  g: number,
  b: number,
  opacity: number
): void {
  const dotRadius = 1.4
  // Dot centers at grid points; wrapping handles tiling automatically
  const centers = [
    [0, 0],
    [size / 2, size / 2],
  ]

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let minDist = Infinity
      for (const [cx, cy] of centers) {
        // Check wrapped distances for seamless tiling
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const ddx = x - (cx + dx * size)
            const ddy = y - (cy + dy * size)
            const dist = Math.sqrt(ddx * ddx + ddy * ddy)
            if (dist < minDist) {
              minDist = dist
            }
          }
        }
      }

      if (minDist < dotRadius + 0.5) {
        const alpha = minDist <= dotRadius ? 1.0 : dotRadius + 0.5 - minDist
        const a = Math.round(alpha * opacity * 255)
        if (a > 0) {
          const idx = (y * size + x) * 4
          pixels[idx] = r
          pixels[idx + 1] = g
          pixels[idx + 2] = b
          pixels[idx + 3] = a
        }
      }
    }
  }
}

export function getPatternImageId(
  pattern: FillPatternType,
  color: string,
  opacity: number
): string {
  return `fill-pattern-${pattern}-${color.replace('#', '')}-${Math.round(opacity * 100)}`
}

/**
 * Apply a fill pattern to a map layer. Creates a canvas-based pattern image
 * and sets it as the fill-pattern on the given layer.
 */
export function applyFillPatternToLayer(
  map: maplibregl.Map,
  fillLayerId: string,
  pattern: FillPatternType | undefined,
  color: string,
  opacity: number
): void {
  if (!map.getLayer(fillLayerId)) {
    return
  }

  // For solid or none, remove any pattern and use regular fill
  if (!pattern || pattern === 'solid' || pattern === 'none') {
    try {
      // Remove fill-pattern by setting to null (MapLibre removes the property)
      map.setPaintProperty(fillLayerId, 'fill-pattern', null as string | null)
    } catch {
      // fill-pattern may not have been set
    }
    return
  }

  const imageId = getPatternImageId(pattern, color, opacity)

  // Register the pattern image if not already done
  if (!map.hasImage(imageId)) {
    const { data, size } = createPatternData(pattern, color, opacity)
    map.addImage(imageId, { width: size, height: size, data })
    registeredPatterns.add(imageId)
  }

  // Set the fill pattern on the layer
  map.setPaintProperty(fillLayerId, 'fill-pattern', imageId)
  // When using fill-pattern, fill-color and fill-opacity are ignored by MapLibre,
  // so the pattern canvas itself encodes the color and opacity
}

/**
 * Remove a fill pattern from a layer, reverting to solid fill.
 */
export function removeFillPattern(map: maplibregl.Map, fillLayerId: string): void {
  if (!map.getLayer(fillLayerId)) {
    return
  }
  try {
    map.setPaintProperty(fillLayerId, 'fill-pattern', null as string | null)
  } catch {
    // fill-pattern may not have been set
  }
}
